import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Search } from "lucide-react";
import type { ChannelMetadata } from "@/types";
import {
  getChannelTint,
  type ChannelColorIndices,
  type CHANNEL_TINTS,
} from "./channelColors";

interface CompactChannelSelectorProps {
  channels: Record<string, ChannelMetadata>;
  selectedChannels: string[];
  onSelectionChange: (channels: string[]) => void;
  channelColorIndices?: ChannelColorIndices;
  maxSelections?: number;
  className?: string;
}

interface SortableActiveChannelProps {
  name: string;
  unit: string;
  tint: (typeof CHANNEL_TINTS)[number];
  canReorder: boolean;
  onToggle: (name: string) => void;
}

function SortableActiveChannel({
  name,
  unit,
  tint,
  canReorder,
  onToggle,
}: SortableActiveChannelProps) {
  const { t } = useTranslation(["pages", "common"]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name, disabled: !canReorder });

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      className="w-full flex items-center gap-1.5 rounded-lg mb-1.5 text-left"
      style={{
        ...sortableStyle,
        padding: "8px 8px",
        background: tint.bg,
        border: `1px solid ${tint.border}`,
      }}
    >
      <button
        type="button"
        disabled={!canReorder}
        className="p-0.5 rounded cursor-grab active:cursor-grabbing disabled:cursor-default"
        style={{
          color: canReorder ? "#4b5563" : "#1f2937",
          flexShrink: 0,
          touchAction: "none",
        }}
        title={t("pages:sessions.telemetry.channelSelector.dragToReorder")}
        aria-label={t("pages:sessions.telemetry.channelSelector.reorderAria", { name })}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onToggle(name)}
        className="flex-1 min-w-0 flex items-center gap-2.5 text-left"
      >
        <div
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: tint.dot,
            boxShadow: `0 0 6px ${tint.glow}`,
            flexShrink: 0,
          }}
        />
        <div className="flex-1 min-w-0">
          <span style={{ fontSize: 12, fontWeight: 500, color: tint.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {name}
          </span>
        </div>
        {unit && (
          <span style={{ fontSize: 10, color: "#4b5563", fontFamily: "monospace", flexShrink: 0 }}>
            {unit}
          </span>
        )}
      </button>
    </div>
  );
}

export const CompactChannelSelector: React.FC<CompactChannelSelectorProps> = ({
  channels,
  selectedChannels,
  onSelectionChange,
  channelColorIndices,
  maxSelections = 8,
  className = "",
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const [searchTerm, setSearchTerm] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const availableChannels = useMemo(() => {
    const all = Object.keys(channels).filter((n) => n !== "Time");
    const term = searchTerm.toLowerCase();
    const unselected = all.filter((n) => !selectedChannels.includes(n));
    if (!term) return unselected;
    return unselected.filter(
      (n) =>
        n.toLowerCase().includes(term) ||
        channels[n]?.description?.toLowerCase().includes(term),
    );
  }, [channels, selectedChannels, searchTerm]);

  const toggleChannel = (name: string) => {
    if (selectedChannels.includes(name)) {
      onSelectionChange(selectedChannels.filter((c) => c !== name));
    } else if (selectedChannels.length < maxSelections) {
      onSelectionChange([...selectedChannels, name]);
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = selectedChannels.indexOf(String(active.id));
    const newIndex = selectedChannels.indexOf(String(over.id));

    if (oldIndex === -1 || newIndex === -1) return;
    onSelectionChange(arrayMove(selectedChannels, oldIndex, newIndex));
  };

  return (
    <div className={`flex flex-col h-full ${className}`} style={{ background: "#0d0d14" }}>
      {/* Header */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{ padding: "10px 12px 8px", borderBottom: "1px solid #1a1a2e", background: "#111827" }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "#4b5563" }}>
          {t("pages:sessions.telemetry.channelSelector.channels")}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#4b6bfb", fontFamily: "monospace" }}>
          {selectedChannels.length}/{maxSelections}
        </span>
      </div>

      {/* Search */}
      <div className="flex-shrink-0" style={{ padding: "8px 10px", borderBottom: "1px solid #1a1a2e" }}>
        <div
          className="flex items-center gap-2 px-2 py-1.5 rounded-md"
          style={{ background: "#0a0a0f", border: "1px solid #1f2937" }}
        >
          <Search className="w-3 h-3 flex-shrink-0" style={{ color: "#4b5563" }} />
          <input
            type="text"
            placeholder={t("pages:sessions.telemetry.channelSelector.filterPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent outline-none"
            style={{ fontSize: 12, color: "#94a3b8" }}
          />
        </div>
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "8px" }}>
        {/* Active channels */}
        {selectedChannels.length > 0 && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "#374151",
                marginBottom: 6,
                padding: "0 4px",
              }}
            >
              {t("pages:sessions.telemetry.channelSelector.active")}
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedChannels}
                strategy={verticalListSortingStrategy}
              >
                {selectedChannels.map((name, idx) => (
                  <SortableActiveChannel
                    key={name}
                    name={name}
                    unit={channels[name]?.unit ?? ""}
                    tint={getChannelTint(name, channelColorIndices, idx)}
                    canReorder={selectedChannels.length > 1}
                    onToggle={toggleChannel}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}

        {/* Available channels */}
        {availableChannels.length > 0 && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "#374151",
                marginBottom: 6,
                marginTop: selectedChannels.length > 0 ? 10 : 0,
                padding: "0 4px",
              }}
            >
              {searchTerm ? t("pages:sessions.telemetry.channelSelector.results") : t("pages:sessions.telemetry.channelSelector.available")}
            </div>
            {availableChannels.map((name) => {
              const unit = channels[name]?.unit ?? "";
              const disabled = selectedChannels.length >= maxSelections;
              return (
                <button
                  key={name}
                  onClick={() => toggleChannel(name)}
                  disabled={disabled}
                  className="w-full flex items-center gap-2.5 rounded-md mb-1 text-left"
                  style={{
                    padding: "6px 10px",
                    opacity: disabled ? 0.4 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#1f2937",
                      border: "1px solid #374151",
                      flexShrink: 0,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <span style={{ fontSize: 12, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                      {name}
                    </span>
                  </div>
                  {unit && (
                    <span style={{ fontSize: 10, color: "#374151", fontFamily: "monospace", flexShrink: 0 }}>
                      {unit}
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}

        {availableChannels.length === 0 && searchTerm && (
          <p style={{ fontSize: 12, color: "#374151", padding: "8px 4px" }}>
            {t("pages:sessions.telemetry.channelSelector.noChannelsMatch", { term: searchTerm })}
          </p>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex-shrink-0 text-center"
        style={{ padding: "6px 10px", borderTop: "1px solid #1a1a2e", background: "#111827", fontSize: 11, color: "#374151" }}
      >
        {t("pages:sessions.telemetry.channelSelector.slotsUsed", { selected: selectedChannels.length, max: maxSelections })}
      </div>
    </div>
  );
};
